import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { GoogleMap, PolylineF, MarkerF, useJsApiLoader, InfoWindowF } from '@react-google-maps/api';
import { OPCIONES_GOOGLE_MAPS } from '../../mapas/opcionesGoogleMaps';
import axios from 'axios';
import moment from 'moment';
import './ReporteEventosPosiciones.scss';
import { BASE_URL } from '../../services/ApiService/ApiConfig';

const URL_BASE = 'http://localhost:8443';
const URL_CONFIG_EVENTOS = `${BASE_URL}/configuracion/configuracion-det/configuracion-cab/codigo/ALE-0000-0001`;
const URL_DEVICES = `${BASE_URL}/traccar/devices`;
const URL_POSITIONS_RANGE = `${BASE_URL}/traccar/positions/by-range`;

const getAuthHeaders = () => {
    const token = localStorage.getItem('jwt')?.replaceAll('"', '') || '';
    return {
        headers: {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Headers': '*',
            Authorization: 'Bearer ' + token
        }
    };
};

const ReporteEventosPosiciones = () => {
    const defaultStartTime = new Date();
    defaultStartTime.setMinutes(defaultStartTime.getMinutes() - 30);
    const defaultEndTime = new Date();

    const [dispositivos, setDispositivos] = useState([]);
    const [eventosConfig, setEventosConfig] = useState([]);

    const [selectedDevice, setSelectedDevice] = useState(null);
    const [startDate, setStartDate] = useState(defaultStartTime);
    const [endDate, setEndDate] = useState(defaultEndTime);
    const [selectedEvents, setSelectedEvents] = useState(null);

    const [positions, setPositions] = useState([]);
    const [filteredPositions, setFilteredPositions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showMap, setShowMap] = useState(false);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [selectedJson, setSelectedJson] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');

    const toast = useRef(null);
    const dt = useRef(null);
    const mapRef = useRef(null);

    const { isLoaded } = useJsApiLoader(OPCIONES_GOOGLE_MAPS);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        try {
            // Load events configuration
            const resEvents = await axios.get(URL_CONFIG_EVENTOS, getAuthHeaders());
            if (resEvents.data && resEvents.data.data) {
                // Assuming data is an array of configuracion-det
                const eventsMapped = resEvents.data.data.map(ev => ({
                    label: ev.nombre,
                    value: ev.parametro, // Contains json attribute representation string e.g. "cable:sacado"
                    id: ev.configuracionDetId
                }));
                setEventosConfig(eventsMapped);
            }

            // Load devices
            const resDevices = await axios.get(URL_DEVICES, getAuthHeaders());
            if (resDevices.data && resDevices.data.data) {
                const devsMapped = resDevices.data.data.map(dev => ({
                    label: `${dev.name} (${dev.uniqueid})`,
                    value: dev.uniqueid
                }));
                setDispositivos(devsMapped);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cargar los datos iniciales.', life: 3000 });
        }
    };

    const handleSearch = async () => {
        if (!selectedDevice) {
            toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Seleccione un dispositivo.', life: 3000 });
            return;
        }
        if (!startDate || !endDate) {
            toast.current.show({ severity: 'warn', summary: 'Advertencia', detail: 'Seleccione las fechas.', life: 3000 });
            return;
        }

        setLoading(true);
        try {
            const formatStr = 'YYYY-MM-DD HH:mm:ss';
            const fStart = moment(startDate).format(formatStr);
            const fEnd = moment(endDate).format(formatStr);

            const res = await axios.get(URL_POSITIONS_RANGE, {
                ...getAuthHeaders(),
                params: {
                    deviceId: selectedDevice,
                    startTime: fStart,
                    endTime: fEnd
                }
            });

            if (res.data && res.data.data) {
                const fetchedPositions = res.data.data.sort((a, b) => new Date(a.devicetime) - new Date(b.devicetime));
                setPositions(fetchedPositions);
                applyFilters(fetchedPositions, selectedEvents);
            } else {
                setPositions([]);
                setFilteredPositions([]);
            }
        } catch (error) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error al consultar posiciones.', life: 3000 });
        } finally {
            setLoading(false);
        }
    };

    const applyFilters = (allPositions, events) => {
        if (!events || events.length === 0) {
            setFilteredPositions(allPositions);
            return;
        }

        const filtered = allPositions.filter(pos => {
            if (!pos.attributes) return false;
            let attrsObj = {};
            try {
                attrsObj = typeof pos.attributes === 'string' ? JSON.parse(pos.attributes) : pos.attributes;
            } catch (e) {
                // Fallback to string match if JSON is invalid
                return events.some(evValue => pos.attributes.includes(evValue));
            }

            return events.some(eventId => {
                const evConfig = eventosConfig.find(e => e.id === eventId);
                const evValue = evConfig ? evConfig.value : null;

                if (!evValue) return false;

                // Check if evValue follows key:value pattern
                if (evValue.includes(':')) {
                    const parts = evValue.split(':');
                    const key = parts[0];
                    const val = parts.slice(1).join(':'); // Handle multiple colons if any

                    // Match key and value exactly (loose equality for numbers/strings)
                    return attrsObj.hasOwnProperty(key) && String(attrsObj[key]) === val;
                }
                // Fallback to substring match in the raw attributes string
                return pos.attributes.includes(evValue);
            });
        });

        setFilteredPositions(filtered);
    };

    useEffect(() => {
        applyFilters(positions, selectedEvents);
    }, [selectedEvents, positions]);

    const dateTemplate = (rowData) => {
        return moment(rowData.devicetime).format('YYYY-MM-DD HH:mm:ss');
    };

    const speedTemplate = (rowData) => {
        return typeof rowData.speed === 'number' ? rowData.speed.toFixed(3) : rowData.speed;
    };

    const mapTemplate = (rowData) => {
        const url = `https://www.google.com/maps?q=${rowData.latitude},${rowData.longitude}`;
        return (
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#1a73e8', fontWeight: 'bold' }}>
                <i className="pi pi-map-marker" style={{ marginRight: '5px' }}></i>
                Mapa
            </a>
        );
    };

    const eventTemplate = (rowData) => {
        if (!eventosConfig || eventosConfig.length === 0) return null;

        let attrsObj = {};
        try {
            attrsObj = typeof rowData.attributes === 'string' ? JSON.parse(rowData.attributes) : rowData.attributes;
        } catch (e) {
            // Fallback to searching in string attributes
            const matched = eventosConfig.filter(ev => ev.value && rowData.attributes.includes(ev.value));
            if (matched.length === 0) return null;
            return renderTags(matched);
        }

        const matchedEvents = eventosConfig.filter(ev => {
            const evValue = ev.value;
            if (!evValue) return false;

            if (evValue.includes(':')) {
                const parts = evValue.split(':');
                const key = parts[0];
                const val = parts.slice(1).join(':');
                return attrsObj.hasOwnProperty(key) && String(attrsObj[key]) === val;
            }
            return rowData.attributes.includes(evValue);
        });

        return renderTags(matchedEvents);
    };

    const renderTags = (matchedEvents) => {
        // Vibrant and distinct 10-color palette for maximum differentiation
        const colorPalette = [
            '#6366F1', // Indigo
            '#22C55E', // Green
            '#F97316', // Orange
            '#3B82F6', // Blue
            '#EAB308', // Gold
            '#EF4444', // Red
            '#8B5CF6', // Violet
            '#EC4899', // Pink
            '#06B6D4', // Cyan
            '#10B981'  // Emerald
        ];

        return (
            <div className="flex flex-wrap gap-1">
                {matchedEvents.map((ev) => {
                    const configIndex = eventosConfig.findIndex(c => c.id === ev.id);
                    const bgColor = colorPalette[configIndex % colorPalette.length];
                    return (
                        <span key={ev.id} className="p-tag" style={{
                            fontSize: '10px',
                            padding: '0.2rem 0.5rem',
                            backgroundColor: bgColor,
                            color: '#fff',
                            textShadow: '0 1px 1px rgba(0,0,0,0.2)',
                            borderRadius: '4px'
                        }}>
                            {ev.label}
                        </span>
                    );
                })}
            </div>
        );
    };

    const exportExcel = () => {
        import('xlsx').then(xlsx => {
            const deviceLabel = dispositivos.find(d => d.value === selectedDevice)?.label || selectedDevice;
            const rangeInfo = `Desde: ${moment(startDate).format('YYYY-MM-DD HH:mm:ss')} Hasta: ${moment(endDate).format('YYYY-MM-DD HH:mm:ss')}`;

            const headerData = [
                ["REPORTE DE EVENTOS DE POSICIONES"],
                ["Dispositivo:", deviceLabel],
                ["Rango:", rangeInfo],
                ["Filtro Eventos:", selectedEvents && selectedEvents.length > 0 ? selectedEvents.length : "Ninguno (Mostrando Todo)"],
                ["Total Registros:", filteredPositions.length],
                [] // Empty row
            ];

            const bodyData = filteredPositions.map(pos => {
                let attrsObj = {};
                try { attrsObj = typeof pos.attributes === 'string' ? JSON.parse(pos.attributes) : pos.attributes; } catch(e){}

                const matchedStr = eventosConfig
                    .filter(ev => {
                        const evValue = ev.value;
                        if (!evValue) return false;
                        if (evValue.includes(':')) {
                            const [key, val] = evValue.split(':');
                            return attrsObj.hasOwnProperty(key) && String(attrsObj[key]) === val;
                        }
                        return pos.attributes.includes(evValue);
                    })
                    .map(ev => ev.label)
                    .join(", ");

                return {
                    "Fecha Dispositivo": moment(pos.devicetime).format('YYYY-MM-DD HH:mm:ss'),
                    "Eventos Detectados": matchedStr,
                    "Latitud": pos.latitude,
                    "Longitud": pos.longitude,
                    "Velocidad": typeof pos.speed === 'number' ? pos.speed.toFixed(3) : pos.speed,
                    "Punto Cercano": pos.nearpoint,
                    "Link Google Maps": `https://www.google.com/maps?q=${pos.latitude},${pos.longitude}`,
                    "Atributos (JSON)": pos.attributes
                };
            });

            const worksheet = xlsx.utils.json_to_sheet([]);
            xlsx.utils.sheet_add_aoa(worksheet, headerData);
            xlsx.utils.sheet_add_json(worksheet, bodyData, { origin: 'A7', skipHeader: false });

            const colWidths = [
                { wch: 20 }, // Fecha
                { wch: 30 }, // Eventos
                { wch: 15 }, // Lat
                { wch: 15 }, // Long
                { wch: 10 }, // Speed
                { wch: 35 }, // NearPoint
                { wch: 45 }, // Link Maps
                { wch: 50 }  // Attributes
            ];
            worksheet['!cols'] = colWidths;

            const workbook = { Sheets: { 'Reporte': worksheet }, SheetNames: ['Reporte'] };
            const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
            saveAsExcelFile(excelBuffer, 'reporte_eventos_posiciones');
        });
    };

    const saveAsExcelFile = (buffer, fileName) => {
        import('file-saver').then(module => {
            if (module && module.default) {
                let EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                let EXCEL_EXTENSION = '.xlsx';
                const data = new Blob([buffer], {
                    type: EXCEL_TYPE
                });
                module.default.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
            }
        });
    };

    const validMapPositions = positions.filter(p =>
        p.latitude != null &&
        p.longitude != null &&
        Number(p.latitude) !== 0 &&
        Number(p.longitude) !== 0 &&
        !isNaN(Number(p.latitude)) &&
        !isNaN(Number(p.longitude))
    );

    const onMapLoad = (map) => {
        mapRef.current = map;
        // Immediate fit on load
        if (validMapPositions.length > 0 && window.google) {
            const bounds = new window.google.maps.LatLngBounds();
            validMapPositions.forEach(p => bounds.extend({ lat: Number(p.latitude), lng: Number(p.longitude) }));
            map.fitBounds(bounds);
        }
    };

    const fitMapBounds = () => {
        if (mapRef.current && validMapPositions.length > 0 && window.google) {
            const bounds = new window.google.maps.LatLngBounds();
            validMapPositions.forEach(p => bounds.extend({ lat: Number(p.latitude), lng: Number(p.longitude) }));
            mapRef.current.fitBounds(bounds);
        }
    };

    const attributesTemplate = (rowData) => {
        const text = rowData.attributes || '';
        const display = text.length > 50 ? text.substring(0, 50) + '...' : text;
        return (
            <div className="flex align-items-center justify-content-between">
                <span className="text-xs text-500 font-monospace" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{display}</span>
                <Button icon="pi pi-eye" className="p-button-text p-button-sm ml-2" onClick={() => setSelectedJson(text)} tooltip="Ver Detalle JSON" />
            </div>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:align-items-center justify-content-between gap-3">
            <div className="flex align-items-center gap-2">
                <Button type="button" icon="pi pi-file-excel" onClick={exportExcel} className="p-button-success" tooltip="Exportar Excel" />
                <Button type="button" icon="pi pi-map" onClick={() => setShowMap(true)} className="p-button-info" tooltip="Ver Mapa General" disabled={filteredPositions.length === 0} />
            </div>

            <div className="flex flex-column md:flex-row align-items-center gap-2 flex-grow-1 justify-content-end">
                <div className="w-full md:w-20rem">
                    <MultiSelect value={selectedEvents} options={eventosConfig} onChange={(e) => setSelectedEvents(e.value)}
                        placeholder="Filtrar por Eventos" filter display="chip" optionValue="id" className="w-full" />
                </div>
                <span className="p-input-icon-left w-full md:w-20rem">
                    <i className="pi pi-search" />
                    <InputText type="search" value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Búsqueda global..." className="w-full" />
                </span>
                <span className="ml-3 text-sm text-600 font-bold white-space-nowrap">Registros: {filteredPositions.length}</span>
            </div>
        </div>
    );


    return (
        <div className="grid">
            <Toast ref={toast} />
            <div className="col-12">
                <div className="card">
                    <h4>Reporte de Eventos de Posiciones</h4>
                    <div className="p-fluid formgrid grid">
                        <div className="field col-12 md:col-4">
                            <label htmlFor="device">Dispositivo</label>
                            <Dropdown id="device" value={selectedDevice} options={dispositivos} onChange={(e) => setSelectedDevice(e.value)} filter placeholder="Seleccione Dispositivo" />
                        </div>
                        <div className="field col-12 md:col-4">
                            <label htmlFor="startDate">Fecha Inicio</label>
                            <Calendar id="startDate" value={startDate} onChange={(e) => setStartDate(e.value)} showTime showSeconds />
                        </div>
                        <div className="field col-12 md:col-4">
                            <label htmlFor="endDate">Fecha Fin</label>
                            <Calendar id="endDate" value={endDate} onChange={(e) => setEndDate(e.value)} showTime showSeconds />
                        </div>
                        <div className="field col-12 md:col-12 mt-3 flex justify-content-end">
                            <Button label="Buscar" icon="pi pi-search" className="p-button-primary w-auto" onClick={handleSearch} loading={loading} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="col-12">
                <div className="card">
                    <DataTable ref={dt} value={filteredPositions} paginator rows={20} rowsPerPageOptions={[10, 20, 50, 100]}
                        loading={loading} emptyMessage="No se encontraron registros." responsiveLayout="scroll"
                        header={header} globalFilter={globalFilter}>
                        <Column field="devicetime" header="Fecha Dispositivo" body={dateTemplate} style={{ minWidth: '150px' }} sortable filter filterPlaceholder="Filtrar fecha"></Column>
                        <Column header="Evento Detectado" body={eventTemplate} style={{ minWidth: '150px' }} filter filterPlaceholder="Filtrar evento"></Column>
                        <Column field="latitude" header="Latitud" style={{ minWidth: '100px' }}></Column>
                        <Column field="longitude" header="Longitud" style={{ minWidth: '100px' }}></Column>
                        <Column header="Ubicación" body={mapTemplate} style={{ minWidth: '100px' }}></Column>
                        <Column field="speed" header="Velocidad" body={speedTemplate} style={{ minWidth: '100px' }} sortable></Column>
                        <Column field="nearpoint" header="Punto Cercano" style={{ minWidth: '200px' }} sortable filter filterPlaceholder="Filtrar lugar"></Column>
                        <Column header="Atributos (JSON)" body={attributesTemplate} style={{ minWidth: '200px' }}></Column>
                    </DataTable>
                </div>
            </div>

            <Dialog header={`Visor de Recorrido General (${validMapPositions.length} puntos de ${positions.length})`} visible={showMap} style={{ width: '90vw' }} onHide={() => { setShowMap(false); setSelectedMarker(null); }} onShow={() => setTimeout(fitMapBounds, 200)}>
                {isLoaded && validMapPositions.length > 0 ? (
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '750px' }}
                        center={{ lat: Number(validMapPositions[0].latitude), lng: Number(validMapPositions[0].longitude) }}
                        zoom={14}
                        onLoad={onMapLoad}
                    >
                        <PolylineF
                            path={validMapPositions.map(p => ({ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }))}
                            options={{
                                strokeColor: "#FF0000",
                                strokeOpacity: 1.0,
                                strokeWeight: 4,
                            }}
                        />
                        {validMapPositions.map((p, index) => (
                            <MarkerF
                                key={`m-${index}-${p.latitude}-${p.longitude}`}
                                position={{ lat: parseFloat(p.latitude), lng: parseFloat(p.longitude) }}
                                label={(index + 1).toString()}
                                title={`Secuencia: ${index + 1}`}
                                onClick={() => setSelectedMarker({ ...p, mapIndex: index + 1 })}
                            />
                        ))}

                        {selectedMarker && (
                            <InfoWindowF
                                position={{ lat: parseFloat(selectedMarker.latitude), lng: parseFloat(selectedMarker.longitude) }}
                                onCloseClick={() => setSelectedMarker(null)}
                            >
                                <div style={{ padding: '5px', maxWidth: '300px' }}>
                                    <div className="flex align-items-center mb-2">
                                        <i className="pi pi-info-circle mr-2 text-primary"></i>
                                        <strong>Punto #{selectedMarker.mapIndex}</strong>
                                    </div>
                                    <div className="mb-1"><small><strong>Fecha:</strong> {moment(selectedMarker.devicetime).format('YYYY-MM-DD HH:mm:ss')}</small></div>
                                    <div className="mb-2"><small><strong>Velocidad:</strong> {selectedMarker.speed?.toFixed(3)} km/h</small></div>
                                    <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '5px' }}>
                                        <small><strong>Atributos:</strong></small>
                                        <div style={{ fontSize: '10px', whiteSpace: 'pre-wrap', margin: "5px 0 0 0", background: '#f8f9fa', padding: '5px', borderRadius: '4px', overflow: 'auto', maxHeight: '150px' }}>
                                            {selectedMarker.attributes}
                                        </div>
                                    </div>
                                </div>
                            </InfoWindowF>
                        )}
                    </GoogleMap>
                ) : (
                    <div className="text-center py-5">
                        <i className="pi pi-map-marker text-4xl mb-3 text-400"></i>
                        <p>{isLoaded ? "No hay posiciones GPS válidas para mostrar en el mapa." : "Cargando mapa..."}</p>
                    </div>
                )}
            </Dialog>

            <Dialog header="Detalle de Atributos (JSON)" visible={!!selectedJson} style={{ width: '50vw' }} onHide={() => setSelectedJson(null)}>
                <div style={{ background: '#2d3436', padding: '20px', borderRadius: '8px', border: '1px solid #1a1a1b', boxShadow: 'inset 0 0 10px rgba(0,0,0,0.5)' }}>
                    <pre style={{ margin: 0, fontSize: '14px', whiteSpace: 'pre-wrap', color: '#fab1a0', fontFamily: 'monospace', lineHeight: '1.5' }}>
                        {(() => {
                            try {
                                return JSON.stringify(typeof selectedJson === 'string' ? JSON.parse(selectedJson) : selectedJson, null, 4);
                            } catch (e) {
                                return selectedJson;
                            }
                        })()}
                    </pre>
                </div>
            </Dialog>
        </div>
    );
};

export default ReporteEventosPosiciones;
